import {Component, DestroyRef, inject, Inject, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

import {
    CampaignProfilePrlObj,
    IdNameMxObj,
    InvocationCodeParamsMxObj,
    KeyValueObj,
    MX_TRANSLATE_SERVICE_TOKEN,
    MxButtonModel,
    MxButtonModule,
    MxConfirmDialogComponent,
    MxDynamicFieldsModule,
    MxInfoTooltipModule,
    MxSharedModule,
    MxTranslateService
} from 'mx-core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import html2pdf from 'html2pdf.js';
import {FormBuilder, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatCard, MatCardContent, MatCardHeader, MatCardTitle} from '@angular/material/card';
import {PrlLanguageModule} from '../../../prl-language/prl-language.module';
import {PrlProfilesService} from '../../../../shared/services/prl-profiles.service';
import {PrlMarketingToolsService} from '../../services/prl-marketing-tools.service';
import {ZoneLightPrlObj} from '../../../../../../generated/generated.model';
import {QRCodeComponent} from 'angularx-qrcode';
import {PrlAddProfileDialogComponent} from '../prl-add-profile-dialog/prl-add-profile-dialog-component';
import {PrlEditVariablesDialogComponent} from '../prl-edit-variables-dialog/prl-edit-variables-dialog-component';
import {Clipboard} from '@angular/cdk/clipboard';


@Component({
    selector: 'prl-generate-code',
    imports: [
        FormsModule,
        MatCard,
        MatCardContent,
        MxButtonModule,
        MxInfoTooltipModule,
        PrlLanguageModule,
        MatCardHeader,
        MatCardTitle,
        ReactiveFormsModule,
        MxSharedModule,
        MxDynamicFieldsModule,
        QRCodeComponent
    ],
    templateUrl: './prl-generate-code.component.html',
    styleUrl: './prl-generate-code.component.scss',
})
export class PrlGenerateCodeComponent implements OnInit {

    public gettingCode = false;
    public gettingEmailTemplate = false;
    public navigatorCanShare = true;
    public showCopyButton = true;

    protected qrCodeLink: string = '';

    protected addProfileButton: MxButtonModel;
    protected cancelButton: MxButtonModel;
    protected copyButton: MxButtonModel;
    protected downloadPdfButton: MxButtonModel;
    protected downloadQrImageButton: MxButtonModel;
    protected openQrCodeButton: MxButtonModel;
    protected shareQrButton: MxButtonModel;
    protected sendEmailButton: MxButtonModel;
    protected updateVars: MxButtonModel;

    protected icType = true;

    protected campaigns: IdNameMxObj[];
    protected landingPageOptions: KeyValueObj[] = [];
    protected icTypeOptions: KeyValueObj[] = [];
    protected allProfiles: Map<number, CampaignProfilePrlObj[]>;
    protected currentCampaignProfiles: IdNameMxObj[] = [];
    protected generateCodeForm = inject(FormBuilder).group({
        campaigns: ['', Validators.required],
        profiles: ['', Validators.required],
        targets: [''],
        landingPages: ['', Validators.required],
        code: [''],
        icTypes: ['', Validators.required],
    });
    protected zone: ZoneLightPrlObj;

    protected icTypes = [
        {id: '1', name: 'label.marketing.tools.javascript.code'}
    ];

    private vars = new Map<string, string>();
    private codeValue = '';

    private readonly destroyRef = inject(DestroyRef);

    constructor(private readonly clipboard: Clipboard,
                private readonly dialog: MatDialog,
                private readonly dialogRef: MatDialogRef<PrlGenerateCodeComponent>,
                private readonly marketingToolsService: PrlMarketingToolsService,
                private readonly profilesService: PrlProfilesService,
                @Inject(MX_TRANSLATE_SERVICE_TOKEN) private readonly translateService: MxTranslateService,
                @Inject(MAT_DIALOG_DATA) public data: {
                    zone: ZoneLightPrlObj
                }
    ) {
        this.zone = data.zone;

    }

    ngOnInit(): void {
        for (let i = 1; i <= 10; i++) {
            this.vars.set(`var${i}`, '');
        }
        this.updateVars = {
            label: () => this.translateService.getTranslatedLabel('label.add.var1'),
            tooltip: () => this.translateService.getTranslatedLabel('label.add.var1'),
            className: 'mxs-button--secondary',
            clickHandler: () => this.editVars()
        };
        this.cancelButton = {
            label: () => this.translateService.getTranslatedLabel('label.cancel'),
            tooltip: () => this.translateService.getTranslatedLabel('label.cancel'),
            className: 'mxs-button--secondary',
            clickHandler: () => this.dialogRef.close(false)
        };
        this.addProfileButton = {
            icon: 'add',
            disabled: false,
            className: 'mxs-button--round  mxs-button--secondary',
            clickHandler: this.addProfile
        };
        this.copyButton = {
            icon: 'content_copy',
            tooltip: 'label.marketing.tools.copy',
            disabled: false,
            className: 'mxs-button--round  mxs-button--secondary',
            clickHandler: this.copyToClipboard
        };
        this.sendEmailButton = {
            icon: 'email',
            tooltip: 'label.marketing.tools.send.email',
            disabled: () => this.gettingEmailTemplate,
            className: 'mxs-button--round  mxs-button--secondary',
            clickHandler: this.prepareEmail
        };
        this.openQrCodeButton = {
            icon: 'public',
            label: 'label.marketing.tools.link.code',
            tooltip: 'label.marketing.tools.link.code',
            className: 'mxs-button--strict',
            disabled: () => this.qrCodeLink === '',
            clickHandler: this.openQrCode
        };
        this.downloadQrImageButton = {
            icon: 'download',
            label: 'label.marketing.tools.download.code',
            tooltip: 'label.marketing.tools.download.code',
            className: 'mxs-button--strict',
            disabled: () => this.qrCodeLink === '',
            clickHandler: this.downloadQrImage
        };
        this.shareQrButton = {
            icon: 'share',
            label: 'label.marketing.tools.share.code',
            tooltip: 'label.marketing.tools.share.code',
            className: 'mxs-button--strict',
            disabled: () => this.qrCodeLink === '',
            clickHandler: this.shareQr
        };
        this.downloadPdfButton = {
            icon: 'picture_as_pdf',
            label: 'label.marketing.tools.download.poster',
            tooltip: 'label.marketing.tools.download.poster',
            className: 'mxs-button--strict',
            disabled: () => this.qrCodeLink === '',
            clickHandler: this.downloadPdf
        };
        if (!this.zone.rotating) {
            this.icTypes.push({id: '2', name: 'label.marketing.tools.html.code'});
            this.generateCodeForm.patchValue({
                    icTypes: this.icTypes[1].id
                }
            );
        } else {
            this.generateCodeForm.patchValue({
                    icTypes: this.icTypes[0].id
                }
            );
        }
        this.campaigns = this.profilesService.getNonExpiredCampaigns();
        this.landingPageOptions = this.toOptions(this.zone.landingPages ?? []);
        this.icTypeOptions = this.icTypes.map(ic => this.toOption(ic.id, this.getLabel(ic.name)));
        this.allProfiles = this.marketingToolsService.getProfiles();
        this.generateCodeForm.patchValue({
                campaigns: this.campaigns[0].id,
                landingPages: this.zone.rotating ? '0' : this.zone.landingPages[0].id
            }
        );
        let campaignId = this.campaigns[0].id;
        this.onCampaignChanged(campaignId);

        this.generateCodeForm.controls.campaigns.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((id: string) => this.onCampaignChanged(id));
        (['profiles', 'landingPages', 'icTypes'] as const).forEach(name =>
            this.generateCodeForm.controls[name].valueChanges
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => this.generateCode())
        );
    }

    protected onCampaignChanged(campaignId: string): void {
        this.currentCampaignProfiles =
            ((this.allProfiles as unknown as Record<string, CampaignProfilePrlObj[]> | undefined)?.[campaignId] ?? []).map(item => {
                const newItem = new IdNameMxObj();
                newItem.id = String(item.id);
                newItem.name = item.name;
                return newItem;
            });
        this.generateCodeForm.patchValue({
                profiles: this.currentCampaignProfiles[0]?.id ?? ''
            }, {emitEvent: false}
        );
        this.generateCode();
    }

    protected addProfile = () => {
        this.dialog.open(PrlAddProfileDialogComponent, {
            width: '40%',
            panelClass: 'mx-dialog-panel-class',
            data: {
                campaignId: this.campaigns[0].id,
            }
        }).afterClosed()
            .subscribe((result: boolean | undefined) => {
                if (result === true) {
                    this.marketingToolsService.loadProfiles().subscribe((profiles) => {
                        this.marketingToolsService.setProfiles(profiles);
                        this.allProfiles = this.marketingToolsService.getProfiles();
                        this.onCampaignChanged(this.generateCodeForm.controls.campaigns.value);
                    });
                }
            });
    };

    protected editVars = () => {
        this.dialog.open(PrlEditVariablesDialogComponent, {
            width: '40%',
            panelClass: 'mx-dialog-panel-class',
            data: {
                vars: this.vars,
            }
        }).afterClosed()
            .subscribe((result: Map<string, string>) => {
                if (result) {
                    this.vars = result;
                    this.generateCode();
                }
            });
    };

    protected getLabel(label: string) {
        return this.translateService.getTranslatedLabel(label);
    }

    protected generateCode = () => {
        this.gettingCode = true;

        const params: Partial<InvocationCodeParamsMxObj> = this.getIcParams();

        this.marketingToolsService
            .getInvocationCode(params)
            .subscribe(code => {
                this.codeValue = code.value;
                this.generateCodeForm.patchValue({
                        code: code.value,
                    }
                );

                this.qrCodeLink = this.getSubstringBetweenQuotes(code.value);
                this.gettingCode = false;
                this.navigatorCanShare = !!navigator.canShare;
            });
    };

    private getIcParams = (): Partial<InvocationCodeParamsMxObj> => {
        const controls = this.generateCodeForm.controls;
        const isHtml = controls.icTypes.value === '2';

        const params: Partial<InvocationCodeParamsMxObj> = {
            profileId: +controls.profiles.value,
            landingPageId: +controls.landingPages.value,
            zoneId: this.zone.id,
            promoId: this.zone.promos[0].id,
            type: isHtml ? 'HTML' : 'JS'
        };

        this.vars.forEach((value, key) => {
            if (value?.trim()) {
                params[key as keyof InvocationCodeParamsMxObj] = value as never;
            }
        });

        return params;
    };

    private copyToClipboard = () => {
        this.clipboard.copy(this.codeValue);
    };

    private prepareEmail = () => {
        this.gettingEmailTemplate = true;
        const params: Partial<InvocationCodeParamsMxObj> = this.getIcParams();

        this.marketingToolsService
            .getInvocationCodeEmailContent(params)
            .subscribe({
                next: content => {
                    this.openMailInClient(atob(content.subject), atob(content.body));
                    this.gettingEmailTemplate = false;
                },
                error: () => {
                    this.gettingEmailTemplate = false;
                }
            });
    };

    private openMailInClient = (subject: string, body: string) => {
        const mailto =
            `mailto:?subject=${encodeURIComponent(subject)}` +
            `&body=${encodeURIComponent(body).replace(/%0A/g, '%0D%0A')}`;

        window.location.href = mailto;
    };

    private openQrCode = () => {
        if (!this.qrCodeLink) {
            return;
        }

        const urlObj = new URL(this.qrCodeLink);
        urlObj.searchParams.append('shareMode', 'true');

        window.open(urlObj.toString(), '_blank');
    };

    private downloadQrImage = () => {
        const canvas = document.querySelector<HTMLCanvasElement>('.qrcode canvas');

        if (!canvas) {
            return;
        }

        const link = document.createElement('a');
        link.href = canvas.toDataURL('image/png');
        link.download = 'qr-image.png';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    private shareQr = () => {
        this.dialog.open(MxConfirmDialogComponent, {
            panelClass: 'mx-dialog-panel-class',
            data: {
                title: this.getLabel('label.marketing.tools.copy.link'),
                message: this.getLabel('label.marketing.tools.copy.message'),
                type: 'warn'
            }
        })
            .afterClosed()
            .subscribe(confirmed => {
                if (!confirmed) {
                    return;
                }

                void navigator.clipboard.writeText(this.qrCodeLink);

                const canvas = document.querySelector<HTMLCanvasElement>('.qrcode canvas');

                if (!canvas) {
                    return;
                }

                canvas.toBlob(blob => {
                    if (!blob) {
                        return;
                    }

                    const file = new File(
                        [blob],
                        'shared-image.png',
                        {type: 'image/png'}
                    );

                    if (navigator.canShare?.({files: [file]})) {
                        void navigator.share({
                            title: this.qrCodeLink,
                            files: [file]
                        });
                    }
                }, 'image/png');
            });
    };

    private downloadPdf = (): void => {
        const element = document.querySelector<HTMLElement>('.qr-preview');

        if (!element) {
            return;
        }

        const clone = element.cloneNode(true) as HTMLElement;

        const originalCanvases = element.querySelectorAll('canvas');
        const clonedCanvases = clone.querySelectorAll('canvas');

        originalCanvases.forEach((originalCanvas, index) => {
            const clonedCanvas = clonedCanvases[index];

            if (!clonedCanvas) {
                return;
            }

            clonedCanvas.width = 520;
            clonedCanvas.height = 520;

            const ctx = clonedCanvas.getContext('2d');

            ctx?.drawImage(
                originalCanvas,
                0,
                0,
                originalCanvas.width,
                originalCanvas.height,
                0,
                0,
                520,
                520
            );
        });

        const wrapper = document.createElement('div');

        wrapper.className = 'pdf-export-wrapper';

        wrapper.innerHTML = `
        <style>
            .pdf-export-wrapper .qr-preview {
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                text-align: center;
                gap: 8px;
                transform: translateX(-12px);
            }

            .pdf-export-wrapper .qr-labels {
                display: flex;
                flex-direction: column;
                align-items: center;
                text-align: center;
            }

            .pdf-export-wrapper .label-link-qr,
            .pdf-export-wrapper .label-link-qr span {
                display: block;
            }

            .pdf-export-wrapper .label-link-qr:first-child,
            .pdf-export-wrapper .label-link-qr:first-child span {
                font-size: 32px !important;
                font-weight: 700 !important;
                line-height: 1.1 !important;
                color: #223a5e !important;
            }

            .pdf-export-wrapper .label-link-qr:last-child,
            .pdf-export-wrapper .label-link-qr:last-child span {
                font-size: 24px !important;
                line-height: 1.2 !important;
                color: #5f6978 !important;
            }

            .pdf-export-wrapper .qrcode {
                margin-top: -20px;
            }

            .pdf-export-wrapper canvas {
                width: 520px !important;
                height: 520px !important;
                display: block;
                margin: 0 auto;
                image-rendering: pixelated;
            }
        </style>
    `;

        wrapper.appendChild(clone);
        wrapper.style.width = '760px';
        wrapper.style.padding = '25px';
        wrapper.style.background = '#ffffff';
        wrapper.style.display = 'flex';
        wrapper.style.justifyContent = 'center';
        wrapper.style.alignItems = 'center';
        wrapper.style.boxSizing = 'border-box';

        // hide on ui
        wrapper.style.position = 'relative';
        wrapper.style.zIndex = '-1';

        document.body.appendChild(wrapper);

        void html2pdf()
            .set({
                filename: 'qr-code.pdf',
                margin: 10,
                image: {
                    type: 'png',
                    quality: 1
                },
                html2canvas: {
                    scale: 3,
                    useCORS: true,
                    backgroundColor: '#ffffff'
                }
            })
            .from(wrapper)
            .save()
            .finally(() => {
                wrapper.remove();
            });
    };

    private getSubstringBetweenQuotes = (str: string): string | null => {
        const regex = /(?<=")[^"]*promoRedirect[^"]*(?=")/g;

        const match = regex.exec(str);
        if (match?.length) {
            return match[0];
        }

        const firstQuote = str.indexOf('"http');
        const secondQuote = str.indexOf('"', firstQuote + 1);

        if (firstQuote !== -1 && secondQuote !== -1) {
            return str.substring(firstQuote + 1, secondQuote);
        }

        return null;
    };

    private toOption(key: string, value: string): KeyValueObj {
        return {key, value} as KeyValueObj;
    }

    private toOptions(items: { id: string, name: string }[]): KeyValueObj[] {
        return items.map(i => this.toOption(i.id, i.name));
    }
}
